import { createGlobalStyle, css } from "styled-components";
import reset from "styled-reset";

/**
 * @description 원시 값을 담고 있는 디자인 토큰 객체
 */
const primitiveTokens = {
  color: {
    white: "#FFFFFF",
    whiteTransparent: {
      "20%": "rgba(238, 225, 229, .2)",
    },

    blackTransparent: {
      "60%": "rgba(0, 0, 0, .6)",
    },

    red: {
      100: "#FFBEC2",
      500: "#E71F2A",
    },

    blue: {
      100: "#E5ECFA",
      500: "#16BE44",
    },

    brick: {
      10: "#FAF6F7",
      100: "#EEE1E5",
      200: "#CFC0C4",
      300: "#A99198",
      400: "#92747C",
      500: "#7B5A63",
      600: "#65434C",
      700: "#4E2D36",
      800: "#3E1924",
      900: "#320B17",
    },

    skyblue: {
      100: "#DEE9F4",
      200: "#C3D4E4",
      300: "#ABC2D6",
      400: "#97B0C7",
      500: "#83A4C2",
      600: "#5C87AE",
      700: "#4C7BA7",
      800: "#396C9C",
      900: "#1C588F",
    },

    ivory: {
      100: "#FBF8F2",
      200: "#F8F4EB",
      300: "#F0EBE1",
      400: "#EBE3D3",
      500: "#E5DAC5",
      600: "#DFCFB1",
      700: "#D7C39D",
      800: "#CDB383",
      900: "#C29C69",
    },

    grey: {
      10: "#FDFDFD",
      100: "#FAFBFD",
      200: "#F3F5F9",
      300: "#E9EBEE",
      400: "#C6C8CD",
      500: "#9AA1AB",
      600: "#666F7B",
      700: "#3A4552",
      800: "#2A323B",
      900: "#171D23",
    },
  },

  typography: {
    web: {
      h1: {
        fontSize: "2.4rem",
        lineHeight: "3.6rem",
      },
      h2: {
        fontSize: "2rem",
        lineHeight: "2.8rem",
      },

      t1: {
        fontSize: "2rem",
        lineHeight: "2.8rem",
      },
      sub1: {
        fontSize: "1.8rem",
        lineHeight: "2.6rem",
      },
      body1: {
        fontSize: "1.6rem",
        lineHeight: "2.4rem",
      },
      body2: {
        fontSize: "1.5rem",
        lineHeight: "2.4rem",
      },
      body3: {
        fontSize: "1.4rem",
        lineHeight: "2.2rem",
      },
      caption1: {
        fontSize: "1.2rem",
        lineHeight: "2rem",
      },
      caption2: {
        fontSize: "1.1rem",
        lineHeight: "1.8rem",
      },
    },
  },
} as const;

// Semantic 토큰
const semanticTokens = {
  color: {
    primary: primitiveTokens.color.brick[700],

    text: {
      default: primitiveTokens.color.grey[900],
      secondary: "#6D757F", // figma에 따로 정의된 디자인 토큰값이 없어서 바로 시멘틱 토큰에 추가
      tertiery: primitiveTokens.color.grey[500],
      white: primitiveTokens.color.white,
      highEmphasis: primitiveTokens.color.brick[700],
      middleEmphasis: primitiveTokens.color.brick[600],
      placeholder: primitiveTokens.color.grey[500],
      disabled: primitiveTokens.color.grey[400],
      danger: primitiveTokens.color.red[500],
    },

    stroke: {
      light: primitiveTokens.color.grey[300],
      dark: primitiveTokens.color.grey[400],
      white: primitiveTokens.color.white,
      primary: primitiveTokens.color.brick[300],
      secondary: primitiveTokens.color.brick[200],
      error: primitiveTokens.color.red[500],
    },

    background: {
      white: primitiveTokens.color.white,
      light: primitiveTokens.color.grey[10],
      default: primitiveTokens.color.grey[100],
      dark: primitiveTokens.color.grey[200],
      primary: primitiveTokens.color.brick[200],
      secondary: primitiveTokens.color.brick[100],
      dimed: primitiveTokens.color.blackTransparent["60%"],
      dimedWhite: primitiveTokens.color.whiteTransparent["20%"],
    },

    button: {
      disabled: primitiveTokens.color.grey[10],
      primary: {
        get enabled() {
          return semanticTokens.color.primary;
        },
        hover: primitiveTokens.color.brick[800],
      },
      secondary: {
        enabled: primitiveTokens.color.brick[100],
        disabled: primitiveTokens.color.brick[200],
      },
      hover: primitiveTokens.color.grey[10],
      white: primitiveTokens.color.white,
    },

    icon: {
      default: primitiveTokens.color.grey[700],
      secondary: primitiveTokens.color.grey[600],
      tertiary: primitiveTokens.color.grey[500],
      white: primitiveTokens.color.white,
      highEmphasis: primitiveTokens.color.brick[500],
      middleEmphasis: primitiveTokens.color.brick[400],
    },

    status: {
      positive: {
        light: primitiveTokens.color.blue[100],
        basic: primitiveTokens.color.blue[500],
      },
      negative: {
        light: primitiveTokens.color.red[100],
        basic: primitiveTokens.color.red[500],
      },
    },
    table: {
      header: primitiveTokens.color.grey[600],
      strip: primitiveTokens.color.grey[200],
    },
  },

  elevation: {
    level1: "0px 10px 30px 0px #0000001a",
  },

  fontWeight: {
    bold: 700,
    semibold: 600,
    medium: 500,
    regular: 400,
  },

  letterSpacing: "-0.03em",

  remToPx: 10,

  breakpoints: {
    small: 96,
    large: 152,
    xlarge: 192,
  },

  zIndex: {
    toast: 99999999,
    modal: 99999800,
    bottomSheet: 99999700,
    dropdown: 99999600,
    drawer: 1000,
    mobileHeader: 998,
    searchBar: 999,
    gnbHeader: 999,
  },
} as const;

/**
 * @description 의미가 정의된 시맨틱 토큰과 컬러 테마를 담고 있는 객체
 */
export const theme = {
  ...semanticTokens,

  color: {
    ...primitiveTokens.color,
    ...semanticTokens.color,
  },

  media: {
    small: `@media screen and (max-width: ${
      semanticTokens.breakpoints.small * semanticTokens.remToPx
    }px)`,
    notSmall: `@media screen and (min-width: ${
      semanticTokens.breakpoints.small * semanticTokens.remToPx + 1
    }px)`,
    large: `@media screen and (max-width: ${
      semanticTokens.breakpoints.large * semanticTokens.remToPx
    }px)`,
    xlarge: `@media screen and (min-width: ${
      semanticTokens.breakpoints.xlarge * semanticTokens.remToPx
    }px)`,
  },

  layout: {
    desktop: {
      lnb: {
        width: "20rem",
      },
      contents: {
        width: `${semanticTokens.breakpoints.large}rem`,

        padding: "3rem 4rem",
      },
    },
  },

  typography: {
    h1: {
      bold: {
        fontWeight: semanticTokens.fontWeight.bold,
        ...primitiveTokens.typography.web.h1,
      },
    },
    h2: {
      bold: {
        fontWeight: semanticTokens.fontWeight.bold,
        ...primitiveTokens.typography.web.h2,
      },
    },

    t1: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.t1,
      },
    },

    sub1: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.sub1,
      },
    },

    body1: {
      bold: {
        fontWeight: semanticTokens.fontWeight.bold,
        ...primitiveTokens.typography.web.body1,
      },
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.body1,
      },
      medium: {
        fontWeight: semanticTokens.fontWeight.medium,
        ...primitiveTokens.typography.web.body1,
      },
      regular: {
        fontWeight: semanticTokens.fontWeight.regular,
        ...primitiveTokens.typography.web.body1,
      },
    },

    body2: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.body2,
      },
      medium: {
        fontWeight: semanticTokens.fontWeight.medium,
        ...primitiveTokens.typography.web.body2,
      },
      regular: {
        fontWeight: semanticTokens.fontWeight.regular,
        ...primitiveTokens.typography.web.body2,
      },
    },

    body3: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.body3,
      },
      medium: {
        fontWeight: semanticTokens.fontWeight.medium,
        ...primitiveTokens.typography.web.body3,
      },
      regular: {
        fontWeight: semanticTokens.fontWeight.regular,
        ...primitiveTokens.typography.web.body3,
      },
    },

    caption1: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.caption1,
      },
      medium: {
        fontWeight: semanticTokens.fontWeight.medium,
        ...primitiveTokens.typography.web.caption1,
      },
      regular: {
        fontWeight: semanticTokens.fontWeight.regular,
        ...primitiveTokens.typography.web.caption1,
      },
    },

    caption2: {
      semibold: {
        fontWeight: semanticTokens.fontWeight.semibold,
        ...primitiveTokens.typography.web.caption2,
      },
      regular: {
        fontWeight: semanticTokens.fontWeight.regular,
        ...primitiveTokens.typography.web.caption2,
      },
    },
  },
} as const;

export const supportDeviceSize = 1080;

export const GlobalStyle = createGlobalStyle`
  ${reset}

  html {
    font-size: 62.5%; // 1rem = 10px 로 변경 한 것
    // 참고링크 = https://stackoverflow.com/questions/68790660/setting-root-font-size-not-affecting-rem-units-in-safari-for-margin-padding-et
    // 128px => 12.8rem , 4px => .4rem 가능!

    scrollbar-gutter: stable both-edges; /* 세로 스크롤바 유무와 무관하게 좌우 여백 대칭 유지 */
  }

  html, body {
    overscroll-behavior: none; /* 스크롤 종료 시 추가 애니메이션 방지 */
  }

  * {
    box-sizing: border-box;
  }

  body {
    background: white;
    margin: 0;
    padding: 0;
    font-size: 160%;
  }

  body, input, a, button {
    font-family: Pretendard, -apple-system, sans-serif, Roboto;
    color: ${({ theme }) => theme.color.text.default};
  }

  #root {
    width: 100%;
  }

  button {
    border:none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;
  }

  a {
    text-decoration: none;
  }

  ${({ theme: { media } }) => css`
    ${media.notSmall} {
      .mobile-only {
        display: none !important;
      }
    }
    ${media.small} {
      .pc-only {
        display: none !important;
      }
    }
  `}
`;
type Theme = typeof theme;

declare module "styled-components" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends Theme {}
}
